import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { from, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, takeUntil, toArray } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { Tools } from 'src/app/classes/shared/tools';
import { trackerConfigCreatedAt, trackerConfigSettingsEntries } from 'src/app/classes/shared/tracker-config-display.util';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { ButtonComponent, ConfirmComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';
import { TrackerConfigDetailDialogComponent, TrackerConfigDetailDialogData } from './tracker-config-detail-dialog/tracker-config-detail-dialog.component';
import { TrackerConfigBulkEditDialogComponent } from './tracker-config-bulk-edit-dialog/tracker-config-bulk-edit-dialog.component';
import { KydConfigureChoiceDialogComponent } from 'src/app/components/project-settings/kyd/kyd-configure-choice-dialog/kyd-configure-choice-dialog.component';

// Devices > engrenage's own manager for TrackerConfig templates - every device type included
// (KYD too, now that it has its own templates - see tracker-config.tool.ts), replacing the old
// ConfigListModalComponent (600px dialog, no filter/sort/multi-select/global view, KYD
// excluded). A full page rather than a dialog: the filtering/sorting/multi-select/detail/bulk
// actions/global view below need the room.
const TYPE_OPTIONS = ['Unit', 'GPS RTK', 'KYD', 'ELSA', 'Tablet', 'Sigfox'];

// Deployments that accumulated a lot of templates under the old (name-every-time) system can
// have hundreds of them - a bulk delete used to fire every DELETE request at once (forkJoin),
// which the backend's rate limiter (429) started rejecting past a certain selection size, with
// the failures silently swallowed by the "success" toast. Capping how many run at a time keeps
// it well under that limit while still being far faster than one at a time.
const BULK_DELETE_CONCURRENCY = 5;

// The grid ("vue globale") inserting every card in one synchronous burst used to freeze the
// whole tab, but only after visiting a project dashboard first. Root cause was leaked Chart.js
// MutationObservers/resize listeners: dashboard widgets (chart.component.ts) already called
// chart.destroy() correctly in ngOnDestroy, but the router was detaching the dashboard route
// instead of destroying it (see reuse-strategy.ts), so that cleanup never ran and every chart
// ever viewed kept re-scanning every future DOM mutation app-wide - fixed there now via
// NO_DETACH_COMPONENTS (component-identity based, so it can't collide with ResourcesComponent/
// InterventionsComponent's own routes, which deliberately still rely on detach-and-reuse).
// Batching the insertion here is kept regardless since it's a reasonable perf smoothing for a
// grid with hundreds of templates either way, not just a leak workaround.
const GRID_RENDER_BATCH_SIZE = 24;

@Component({
  selector: 'app-tracker-configs',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatTableModule,
    MatToolbarModule,
    MatTooltipModule,
    ButtonComponent,
    SearchBarComponent,
    SpinnerComponent,
    NoDataComponent,
  ],
  standalone: true,
  templateUrl: './tracker-configs.component.html',
  styleUrls: ['./tracker-configs.component.scss']
})
export class TrackerConfigsComponent implements OnInit, OnDestroy {
  private readonly trackerService: TrackerService = inject(TrackerService);
  private readonly tools: Tools = inject(Tools);
  private readonly pop: Pop = inject(Pop);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly router: Router = inject(Router);

  private readonly destroy$ = new Subject<void>();

  readonly typeOptions = TYPE_OPTIONS;
  readonly displayedColumns = ['select', 'name', 'type', 'offlineDelay', 'usedBy', 'actions'];

  loading = false;
  configs: TrackerConfig[] = [];
  private trackers: Tracker[] = [];

  // Recomputed by refreshDisplayed()/onXChange() below, NOT a live getter - with a large number
  // of templates, re-filtering/re-sorting on every single change-detection cycle (rather than
  // only when the underlying data or a filter actually changes) was measurably slow enough to
  // make the page feel like it had frozen.
  displayedConfigs: TrackerConfig[] = [];
  // Same reasoning for "which tracker(s) use this template" - an O(trackers) scan per template,
  // per row, per CD cycle - precomputed once into a lookup instead (see recomputeUsedBy()).
  private usedByMap = new Map<string, Tracker[]>();

  typeFilter: string = 'all';
  searchText: string = '';

  // mat-select derives its closed trigger text from the matched mat-option's own rendered
  // content - when that content is itself behind `|translate` (menu.devices.configs.all_types,
  // every sort option), the trigger can end up blank on first load if translations haven't
  // finished loading by the time it first computes that text, and doesn't always recompute it
  // afterward. Bound via <mat-select-trigger> instead (Material's own override for this),
  // recomputed on every change-detection cycle rather than depending on option content at all.
  get typeFilterDisplayText(): string {
    return this.typeFilter === 'all' ? this.translate.instant('menu.devices.configs.all_types') : this.typeFilter;
  }

  get sortOrderDisplayText(): string {
    const key = this.sortOrder === 'name-asc' ? 'menu.devices.configs.sort_name_asc'
      : this.sortOrder === 'name-desc' ? 'menu.devices.configs.sort_name_desc'
      : this.sortOrder === 'created-desc' ? 'menu.devices.configs.sort_created_desc'
      : 'menu.devices.configs.sort_created_asc';
    return this.translate.instant(key);
  }

  private static readonly SORT_ORDER_STORAGE_KEY = 'tracker_configs_sort_order';
  sortOrder: string = localStorage.getItem(TrackerConfigsComponent.SORT_ORDER_STORAGE_KEY) || 'name-asc';

  viewMode: 'table' | 'grid' = 'table';
  multiSelectMode = false;
  // Set by bulkView() - narrows the grid view to the selection without needing a second
  // dedicated dialog/route, cleared as soon as multi-select is turned off.
  showingSelectionOnly = false;

  // TEMPORARY MITIGATION (see GRID_RENDER_BATCH_SIZE above) - what the grid template actually
  // renders, grown towards gridConfigs a batch at a time instead of all at once.
  visibleGridConfigs: TrackerConfig[] = [];
  private gridRenderTimer: ReturnType<typeof setTimeout> | undefined;

  ngOnInit() {
    this.loadConfigs();

    this.trackerService.getTrackers().pipe(takeUntil(this.destroy$)).subscribe((trackers: Tracker[]) => {
      this.trackers = trackers || [];
      this.recomputeUsedBy();
    });
  }

  ngOnDestroy() {
    clearTimeout(this.gridRenderTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadConfigs() {
    this.loading = true;

    this.trackerService.getConfigs().pipe(takeUntil(this.destroy$)).subscribe({
      next: (configs: TrackerConfig[]) => {
        this.configs = configs || [];
        this.recomputeUsedBy();
        this.refreshDisplayed();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  // A template's `settings`/`offlineDelay` say nothing about which capteur(s) it's actually on
  // right now - that lives on the Tracker side, in its `config` link.
  private recomputeUsedBy() {
    const map = new Map<string, Tracker[]>();

    for (const tracker of this.trackers) {
      const id = this.tools.toId(tracker.config);
      if (!id) continue;

      if (!map.has(id)) map.set(id, []);
      map.get(id)!.push(tracker);
    }

    this.usedByMap = map;
  }

  private refreshDisplayed() {
    const search = this.searchText.trim().toLowerCase();
    const filtered = this.configs
      .filter(c => this.typeFilter === 'all' || c.type === this.typeFilter)
      .filter(c => !search || (c.name || '').toLowerCase().includes(search));

    const [field, direction] = this.sortOrder.split('-');
    const factor = direction === 'desc' ? -1 : 1;

    this.displayedConfigs = filtered.sort((a, b) => {
      if (field === 'created') return factor * (trackerConfigCreatedAt(a) - trackerConfigCreatedAt(b));
      return factor * (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
    });

    if (this.viewMode === 'grid') this.startGridRender();
  }

  // TEMPORARY MITIGATION (see GRID_RENDER_BATCH_SIZE above).
  private startGridRender() {
    clearTimeout(this.gridRenderTimer);
    this.visibleGridConfigs = [];
    this.renderNextGridBatch();
  }

  private renderNextGridBatch() {
    const all = this.gridConfigs;
    const nextCount = Math.min(this.visibleGridConfigs.length + GRID_RENDER_BATCH_SIZE, all.length);
    this.visibleGridConfigs = all.slice(0, nextCount);

    if (nextCount < all.length) {
      this.gridRenderTimer = setTimeout(() => this.renderNextGridBatch(), 0);
    }
  }

  // Used by the grid ("vue globale") template - same list as displayedConfigs, narrowed to the
  // selection when opened via bulkView() rather than the header's own view-mode toggle. Cheap
  // enough as a live getter: it only filters the already-computed, already-small(er) result of
  // refreshDisplayed(), not the full underlying dataset.
  get gridConfigs(): TrackerConfig[] {
    return this.showingSelectionOnly ? this.displayedConfigs.filter(c => c.selected) : this.displayedConfigs;
  }

  get selectedConfigs(): TrackerConfig[] {
    return this.configs.filter(c => c.selected);
  }

  get allVisibleSelected(): boolean {
    return this.displayedConfigs.length > 0 && this.displayedConfigs.every(c => c.selected);
  }

  // Separate from allVisibleSelected/toggleSelectAll above (used by the table's own header
  // checkbox) - the grid can be narrowed to just the selection (bulkView()), so "select all"
  // there has to act on whatever it's currently showing (gridConfigs), not always the full
  // displayedConfigs list.
  get allGridSelected(): boolean {
    return this.gridConfigs.length > 0 && this.gridConfigs.every(c => c.selected);
  }

  toggleSelectAllGrid() {
    const selectAll = !this.allGridSelected;
    this.gridConfigs.forEach(c => c.selected = selectAll);
  }

  onTypeFilterChange(value: string) {
    this.typeFilter = value;
    this.refreshDisplayed();
  }

  onSearchChange(value: string) {
    this.searchText = value;
    this.refreshDisplayed();
  }

  onSortOrderChange(value: string) {
    this.sortOrder = value;
    localStorage.setItem(TrackerConfigsComponent.SORT_ORDER_STORAGE_KEY, value);
    this.refreshDisplayed();
  }

  toggleViewMode() {
    this.viewMode = this.viewMode === 'table' ? 'grid' : 'table';
    this.showingSelectionOnly = false;
    if (this.viewMode === 'grid') this.startGridRender();
  }

  toggleMultiSelectMode() {
    this.multiSelectMode = !this.multiSelectMode;
    if (!this.multiSelectMode) this.clearSelection();
  }

  clearSelection() {
    this.configs.forEach(c => c.selected = false);
    this.showingSelectionOnly = false;
  }

  toggleSelectAll() {
    const selectAll = !this.allVisibleSelected;
    this.displayedConfigs.forEach(c => c.selected = selectAll);
  }

  usedBy(config: TrackerConfig): Tracker[] {
    return this.usedByMap.get(config.id) ?? [];
  }

  trackerNames(config: TrackerConfig): string {
    return this.usedBy(config).map(t => t.name).join(', ');
  }

  settingsEntries(config: TrackerConfig): { key: string; value: string }[] {
    return trackerConfigSettingsEntries(config.settings, this.translate, config.type);
  }

  back() {
    this.router.navigate(['/devices']);
  }

  addConfig() {
    this.openDetail(null);
  }

  openDetail(config: TrackerConfig | null, startInEdit: boolean = false) {
    const data: TrackerConfigDetailDialogData = {
      config, trackers: config ? this.usedBy(config) : [], startInEdit, configs: this.configs,
    };

    this.dialog.open(TrackerConfigDetailDialogComponent, { width: '600px', data })
      .afterClosed().subscribe((result: 'saved' | 'deleted' | undefined) => {
        if (result) this.loadConfigs();
      });
  }

  bulkView() {
    this.viewMode = 'grid';
    this.showingSelectionOnly = true;
    this.startGridRender();
  }

  // Gates the "même modification pour tous" choice: a single shared settings form only makes
  // sense when every selected template is the same type (TrackerConfigBulkEditDialogComponent
  // renders one type-specific form, it can't represent a KYD and a Unit at once). A mixed
  // selection still gets "un par un".
  private selectionHasUniformType(configs: TrackerConfig[]): boolean {
    if (!configs.length) return false;
    const first = configs[0]?.type ?? 'Unit';
    return configs.every(c => (c.type ?? 'Unit') === first);
  }

  // Same "même modification pour tous" vs "un par un" choice as resources.component.ts's own
  // bulkEditTrackersDirectly().
  bulkEditSelection() {
    const selection = this.selectedConfigs;
    if (!selection.length) return;

    const choiceRef = this.dialog.open(KydConfigureChoiceDialogComponent, {
      width: '450px',
      data: { count: selection.length, context: 'edit', allowSame: this.selectionHasUniformType(selection) },
      restoreFocus: false
    });

    choiceRef.afterClosed().subscribe(choice => {
      if (choice === 'same') this.bulkEditSameSettings([...selection]);
      else if (choice === 'each') this.bulkEditSequential([...selection]);
    });
  }

  private bulkEditSameSettings(selection: TrackerConfig[]) {
    this.clearSelection();
    this.multiSelectMode = false;

    this.dialog.open(TrackerConfigBulkEditDialogComponent, { width: '600px', data: { configs: selection } })
      .afterClosed().subscribe(saved => { if (saved) this.loadConfigs(); });
  }

  // No single set of values makes sense to apply to several DIFFERENT templates at once (unlike
  // bulk-configuring several trackers with the same values) - this opens each selected template
  // for editing one after another instead, inside a single dialog instance (see
  // TrackerConfigDetailDialogComponent's own queue handling - closing and reopening a new dialog
  // per template used to visibly flash the page underneath between each one).
  private bulkEditSequential(selection: TrackerConfig[]) {
    if (!selection.length) return;

    this.clearSelection();
    this.multiSelectMode = false;

    const data: TrackerConfigDetailDialogData = {
      config: null, trackers: [], queue: selection, usedBy: (config: TrackerConfig) => this.usedBy(config), configs: this.configs,
    };

    this.dialog.open(TrackerConfigDetailDialogComponent, { width: '600px', data })
      .afterClosed().subscribe(() => this.loadConfigs());
  }

  bulkDelete() {
    const selection = this.selectedConfigs;
    if (!selection.length) return;

    this.dialog.open(ConfirmComponent, {
      width: '500px',
      data: {
        icon: 'delete',
        title: this.translate.instant('menu.devices.configs.bulk_delete_confirm_title'),
        content: this.translate.instant('menu.devices.configs.bulk_delete_confirm_content', { count: selection.length }),
        okIcon: 'delete_forever',
        okColor: 'danger',
        okText: 'form.delete',
        cancelText: 'form.cancel',
      }
    }).afterClosed().subscribe(result => {
      if (!result?.ok) return;

      // A handful at a time rather than all at once (see BULK_DELETE_CONCURRENCY) - a large
      // accumulated selection firing every DELETE simultaneously could get some of them 429'd
      // by the backend's rate limiter, silently lost under the "success" toast.
      from(selection).pipe(
        mergeMap(c => this.trackerService.deleteConfig(c.id).pipe(map(() => true), catchError(() => of(false))), BULK_DELETE_CONCURRENCY),
        toArray(),
      ).subscribe(results => {
        const failedCount = results.filter(r => !r).length;

        if (failedCount === 0) {
          this.pop.toast(this.translate.instant('menu.devices.configs.bulk_delete_success', { count: results.length }), 'success', 4000, 'delete');
        } else if (failedCount < results.length) {
          this.pop.warning(this.translate.instant('menu.devices.configs.bulk_delete_partial', { failed: failedCount, total: results.length }));
        } else {
          this.pop.error(this.translate.instant('trackers.error.config_save'));
        }

        this.multiSelectMode = false;
        this.loadConfigs();
      });
    });
  }

  deleteConfig(config: TrackerConfig) {
    this.dialog.open(ConfirmComponent, {
      width: '500px',
      data: {
        icon: 'delete',
        title: this.translate.instant('menu.devices.configs.delete_confirm_title'),
        content: this.translate.instant('menu.devices.configs.delete_confirm_content', { name: config.name }),
        okIcon: 'delete_forever',
        okColor: 'danger',
        okText: 'form.delete',
        cancelText: 'form.cancel',
      }
    }).afterClosed().subscribe(result => {
      if (!result?.ok) return;

      this.trackerService.deleteConfig(config.id).subscribe(() => {
        this.pop.toast(this.translate.instant('units.device.config_removed'), 'success', 4000, 'delete');
        this.loadConfigs();
      });
    });
  }
}
